import { createSlice } from '@reduxjs/toolkit'

import {
  CATEGORIES,
  CATEGORY_KEY_DFLT,
  CATEGORY_KEYS,
  ONBOARDING_CATEGORIES,
  ONBOARDING_CATEGORY_KEY_DFLT,
  SECURITY_AND_COMPLIANCE_ITEMS
} from './categories'
import { getIntegrationSearchData, SEARCH_DATA_FIELD } from './find-integrations'
import {
  getFrontendIntegrationName,
  INTEGRATION_KEYS,
  INTEGRATIONS,
} from './integration-items'
import { TAB_DFLT, TABS } from './tab-items'

const SEARCH_TERM_DFLT = ''

const listSlice = createSlice({
  name: 'list',
  initialState: {
    activeTab: TAB_DFLT,
    categories: CATEGORIES,
    defaultCategory: CATEGORY_KEY_DFLT,
    filterCategory: CATEGORY_KEY_DFLT,
    items: INTEGRATIONS,
    searchTerm: SEARCH_TERM_DFLT,
  },
  reducers: {
    initIntegrations: (state, { payload }) => {
      // Perform any dynamic integration setup that can't be set on the
      // initial state due to async availability of data
      const { userId, userRole, currentTier, slackUrl, itemsToSkip } = payload

      state.items[INTEGRATION_KEYS.slack].href = slackUrl

      SECURITY_AND_COMPLIANCE_ITEMS.forEach((key) => {
        state.items[key] = {
          ...state.items[key],
          segment: {
            ...state.items[key].segment,
            user_id: userId,
            user_role: userRole,
            current_tier: currentTier
          }
        }
      })

      itemsToSkip.forEach((key) => {
        delete state.items[key]
      })

      // Make sure to also remove skipped integrations from the keys listed
      // in the categories integrations lists
      Object.keys(state.categories).forEach((c) => {
        const visibleIntegrations = state.categories[c].integrations.filter((i) => !itemsToSkip.includes(i))
        state.categories[c].integrations = visibleIntegrations
      })

      for (const [name, item] of Object.entries(state.items)) {
        state.items[name][SEARCH_DATA_FIELD] = getIntegrationSearchData(item)
      }
    },
    setActiveTab: (state, { payload }) => {
      const newTab = payload || TAB_DFLT
      const cleanTab = newTab?.trim()
      const valid = cleanTab && (state.activeTab !== cleanTab) && TABS.includes(cleanTab)
      if (valid) {
        state.activeTab = newTab
      }
    },
    setConnectedIntegrations: (state, { payload }) => {
      const integrations = payload || []

      for (const name of Object.keys(state.items)) {
        state.items[name].connected = false
        state.items[name].noPushCalendar = false
      }

      for (const integration of integrations) {
        const name = getFrontendIntegrationName(integration.name)
        if (state.items[name]) {
          state.items[name].connected = integration.connected
          state.items[name].noPushCalendar = integration.noPushCalendar
        }
      }
    },
    setFilterCategory: (state, { payload }) => {
      const newCategory = payload || state.defaultCategory
      const validCategories = Object.keys(state.categories)
      const valid = validCategories.includes(newCategory)
      if (valid) {
        state.filterCategory = newCategory

        if (newCategory !== CATEGORY_KEYS.all) {
          // Clear out the search term, as the search is global and not
          // applicable any more
          state.searchTerm = SEARCH_TERM_DFLT
        }
      }
    },
    setOnboardingCategories: (state) => {
      state.categories = ONBOARDING_CATEGORIES
      state.defaultCategory = ONBOARDING_CATEGORY_KEY_DFLT
      state.filterCategory = ONBOARDING_CATEGORY_KEY_DFLT
    },
    setSearchTerm: (state, { payload }) => {
      const newTerm = (payload || SEARCH_TERM_DFLT).trim()
      if (newTerm !== state.searchTerm) {
        state.searchTerm = newTerm
      }
    },
  },
})

export const {
  initIntegrations,
  setActiveTab,
  setConnectedIntegrations,
  setFilterCategory,
  setOnboardingCategories,
  setSearchTerm
} = listSlice.actions

export const { reducer } = listSlice
